import type { RiskFlowAlert } from "../../lib/riskflow-feed";
import type { TiltScoutInstrumentOutlook } from "./tilt-scout-types";
import { headlineWeightForAlert } from "./tilt-scout-headline-weight";

const MICRO_FUTURES: Record<string, string> = {
  MNQ: "NQ",
  MES: "ES",
  MYM: "YM",
  M2K: "RTY",
  MCL: "CL",
  MGC: "GC",
  SIL: "SI",
};

export function normalizeSymbol(symbol: string): string {
  const raw = symbol.replace("/", "").toUpperCase().trim();
  return MICRO_FUTURES[raw] ?? raw;
}

export function findInstrumentOutlook(
  outlook: TiltScoutInstrumentOutlook[],
  canonicalSymbol: string,
): TiltScoutInstrumentOutlook | null {
  return (
    outlook.find((item) => normalizeSymbol(item.symbol) === canonicalSymbol) ??
    null
  );
}

export function topEvidenceAlerts(
  alerts: RiskFlowAlert[],
  canonicalSymbol: string,
) {
  return alerts
    .filter((alert) => isRelevantAlert(alert, canonicalSymbol))
    .sort((a, b) => alertWeight(b) - alertWeight(a))
    .slice(0, 6);
}

export function alertText(alert: RiskFlowAlert) {
  return [
    alert.headline,
    alert.summary,
    alert.riskType,
    alert.category,
    ...(alert.tags ?? []),
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
}

function isRelevantAlert(alert: RiskFlowAlert, canonicalSymbol: string) {
  const fields = [
    alert.instrument,
    ...(alert.symbols ?? []),
    ...(alert.tags ?? []),
    alert.headline,
    alert.summary,
  ]
    .filter(Boolean)
    .join(" ")
    .toUpperCase();
  if (fields.includes(canonicalSymbol)) return true;
  if (
    ["Macro", "Geopolitical", "Liquidity", "Credit"].includes(
      alert.riskType ?? "",
    )
  )
    return true;
  return (
    headlineWeightForAlert(alert).headlineWeight >= 6 ||
    alert.severity === "critical"
  );
}

function alertWeight(alert: RiskFlowAlert) {
  const impact = headlineWeightForAlert(alert).headlineImpactWeight;
  const severity =
    alert.severity === "critical" ? 2 : alert.severity === "high" ? 1 : 0;
  return impact + severity;
}
